import { clearStickyLiveRunHint, terminateOrphanSubagentsOnChatClose } from "../app-tool-stream.ts";
import type { CompactionStatus } from "../app-tool-stream.ts";
// JAISIU-types-sweep: ChatAttachment comes from the canonical
// narrow type (chat/attachment-types.ts) so call sites can read
// `att.filename` / `att.mime` / `att.sizeBytes`. The wide
// `ui-types.ts::ChatAttachment` (with `dataUrl` / `previewUrl`)
// covers in-flight upload placeholders only; the host state now
// stores the narrow shape end-to-end.
import type { ChatAttachment } from "../chat/attachment-types.ts";
import { chatErrorInputFromEvent, formatStructuredChatErrorPlain } from "../chat/chat-errors.ts";
import { clearChatLastRunSnapshot } from "../chat/chat-last-run-files.ts";
import { clearLiveState, recordLiveState } from "../chat/chat-live-state.ts";
import {
  paintChatMessagesIfActive,
  paintPendingUserMessageIdsIfActive,
  patchChatMessagesForSession,
  readPendingUserMessageIdsForSession,
  resolveLiveMessagesForSessionKey,
  writeChatMessagesForSession,
  writePendingUserMessageIdsForSession,
  type ChatMessagesBySessionCache,
  type PendingUserMessageIdsBySessionCache,
} from "../chat/chat-messages-by-session.ts";
import {
  CHAT_SEND_MAX_RETRIES,
  computeRetryDelayMs,
  formatGatewayErrorForDisplay,
  shouldRetryChatSend,
  sleepMs,
} from "../chat/chat-retry.ts";
import { resolveToolStatusPhrase } from "../chat/chat-run-status-phrases.ts";
import { shouldUseHttpUpload, uploadAttachmentHttp } from "../chat/chat-upload-http.ts";
import { stopChatWaitTicker, syncChatWaitTicker } from "../chat/chat-wait-ticker.ts";
import type { FleetComposerMode } from "../chat/fleet-composer-mode.ts";
import {
  extractAssistantErrorMessage,
  extractText,
  extractThinking,
  findLastAssistantChatError,
} from "../chat/message-extract.ts";
import {
  isAbortRunNotFoundError,
  isGatewayRequestError,
  isSteerNotStreamingError,
} from "../gateway-errors.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { generateUUID } from "../uuid.ts";
import { revertArtifactsSince } from "./session-artifacts-review.ts";
import { loadSessionUploadsIntoState } from "./session-uploads-bridge.ts";
import type { SessionUploadsState } from "./session-uploads.ts";

/**
 * Chat controller host state.
 *
 * JAISIU-types-sweep: `chatLoading`, `chatThinkingLevel`,
 * `pendingUserMessageIds` and `lastError` are host-managed and
 * coalesced with sensible defaults at every controller call site, so
 * they are optional here — the production host (`OpenClawApp`) still
 * populates all four, and the unit-test host fixtures no longer have
 * to restate state the controller only reads.
 */
export type ChatState = {
  /**
   * JAISIU-types-sweep: optional + nullable. `ChatHost`
   * (`app-chat.ts`) declares `client?` because the tab / session
   * helpers run before the gateway socket is up, so the chat host was
   * structurally unable to satisfy a REQUIRED `client` — which made
   * `resetChatSession(this)` in `app.ts::handleResetChatTab` fail to
   * type-check. Every controller entry point already opens with
   * `if (!state.client || !state.connected) return …`, so "absent" and
   * "null" take the identical path; the production host still always
   * populates it.
   */
  client?: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  /**
   * JAISIU-4089 — optional hook the host installs so the chat
   * controller can report transient RPC failures to the
   * session-health monitor. The controller invokes this when a
   * `chat.send` retry exhausts its budget OR the final attempt
   * throws. Optional because the controller is reused in unit
   * tests that don't want the side effect.
   */
  noteSessionHealthFailure?: (reason: string) => void;
  chatHistoryDisplayLimit?: number;
  /**
   * JAISIU-3473 — upward-pagination cursor state. Mirrored on the
   * `OpenClawApp` so the scroll handler and the chat controller agree
   * on which page is loaded.
   *  - `chatHistoryOldestLoadedId` — id of the oldest message currently
   *    rendered, sent back as `before` on the next older-page request.
   *  - `chatHistoryHasMoreOlder` — true when the server has further
   *    pages strictly older than `chatHistoryOldestLoadedId`. Drives
   *    the scroll-up pager's enable/disable state.
   *  - `chatHistoryLoadingOlder` — in-flight guard so rapid scroll
   *    events coalesce into a single older-history request.
   *  - `chatProgrammaticScroll` — flag set while a script adjusts
   *    `scrollTop` (post-prepend anchor restore). The scroll handler
   *    ignores events while this flag is set so the ↑/↓ chevrons are
   *    not armed by the viewport correction itself.
   */
  chatHistoryOldestLoadedId?: string | null;
  chatHistoryHasMoreOlder?: boolean;
  chatHistoryLoadingOlder?: boolean;
  chatProgrammaticScroll?: boolean;
  chatLoading?: boolean;
  chatHistoryPolling?: boolean;
  chatMessages: unknown[];
  chatThinkingLevel?: string | null;
  chatSending: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatRunId: string | null;
  chatStream: string | null;
  chatReasoningStream: string | null;
  chatStreamStartedAt: number | null;
  /**
   * JAISIU-XXXX: timestamp set when an inject-prefixed chat.final lands.
   * The view flashes the live-run bubble for INJECT_BUBBLE_FLASH_MS while
   * `nowMs - chatInjectedAt < INJECT_BUBBLE_FLASH_MS`. A setTimeout clears
   * it after the window so the bubble auto-dismisses.
   */
  chatInjectedAt?: number | null;
  /**
   * JAISIU-XXXX — number of user messages waiting behind an in-flight agent run.
   * Bumped in `sendChatMessage` when the previous run is still active
   * (collect mode) and decremented when that run finalizes. Drives the
   * "Queued (n) — agent will reply when this turn closes" banner under the
   * composer and the per-message ⏳ chip on queued optimistic user bubbles.
   * Kept independent of `chatSending`/`chatRunId` so a brief reconnect or
   * a fast-successive send while no run is active never flashes the chip.
   */
  chatCollectQueueDepth?: number;
  /**
   * Optimistic user-message `runId`s awaiting server reconciliation.
   * Each entry was set when the user posted (so the message could appear
   * immediately) and is removed once `chat.history` returns a server-side
   * message with the same id (the canonical version). Until that happens
   * the message survives `loadChatHistory` polls so it doesn't "blink"
   * off and back on, and image uploads don't disappear while the upload
   * or `chat.send` is still in flight.
   */
  pendingUserMessageIds?: string[];
  /**
   * JAISIU-chatswitch-fastpath — per-session transcript cache. The chat
   * controller write-throughs optimistic rows and history merges here so
   * a tab switch mid-send cannot lose the operator's last message.
   */
  chatMessagesBySession?: ChatMessagesBySessionCache;
  /**
   * Inbound attachments for the open session, shown in the Files tab.
   * Separate from project diffs: an operator who attached an mp4 changed no
   * project file, and the two were previously conflated into one empty tab.
   */
  sessionUploads?: SessionUploadsState;
  /** Mirrors `pendingUserMessageIds` per session key. */
  pendingUserMessageIdsBySession?: PendingUserMessageIdsBySessionCache;
  /**
   * Background run ids (inject / subagent announce) linked to the active
   * parent session. Populated from `chat.run` WS events; used to avoid
   * clearing the parent streaming latch when a sibling final lands.
   */
  sessionBackgroundRuns?: Set<string>;
  /** Lit-reactive mirror of `sessionBackgroundRuns` for render triggers. */
  sessionBackgroundRunIds?: string[];
  /**
   * JAISIU-XXXX / UX-R-XXXX — runIds that the operator has explicitly
   * aborted via the Stop button. Used by `handleChatEvent` to drop
   * trailing `chat.final` events for the same runId (the model may have
   * finished generating before the abort signal landed; without this
   * guard the message would still render as if it completed, hiding the
   * fact that the operator stopped it).
   *
   * Cleared by every successful chat.history poll that returns the
   * server-canonical state for the affected runId, and also on a
   * short TTL so the set can never grow unbounded.
   */
  abortedRunIds?: Set<string>;
  lastError?: string | null;
  chatLiveRunHint?: string | null;
  /** Fleet Control composer mode sent with chat.send. */
  chatComposerMode?: FleetComposerMode;
  /**
   * Monotonic generation bumped on every `loadChatHistory` start and session
   * switch. In-flight history RPCs apply results only when the generation
   * still matches, so a fast A→B tab switch cannot paint chat-A onto chat-B.
   */
  chatHistoryGeneration?: number;
  /**
   * JAISIU-3307 — currently-running `loadChatHistory` abort handle. Hosts
   * that want explicit cancellation (typical: `activateChatSession`) call
   * `controller.abort()` and check `controller.signal.aborted` before
   * mutating state. Hosts that don't wire this signal still benefit from
   * the `chatHistoryGeneration` guard, so leaving it `undefined` is safe.
   */
  chatHistoryAbort?: AbortController | null;
  /**
   * Fleet Control staleness verification.
   * Stamped by `loadChatHistory`, `handleChatEvent`, and `app-gateway.ts`.
   */
  chatSync?: {
    lastSyncedAt: number | null;
    lastEventAt: number | null;
    lastWsCloseAt: number | null;
  };
  /** Path surfaced by the active file tool (composer + live run). */
  chatActiveFileToolPath?: string | null;
  /** Main run card when the parent session is orchestrating subagents. */
  chatMainRun?: ChatSubagentCard | null;
  compactionStatus?: CompactionStatus | null;
  chatSubagents?: ChatSubagentCard[];
};

export type ChatRunEventPayload = {
  runId: string;
  sessionKey: string;
  phase: string;
  origin?: string;
  parentRunId?: string;
  label?: string;
  childSessionKey?: string;
};

export type ChatEventPayload = {
  runId: string;
  sessionKey: string;
  /**
   * JAISIU-types-sweep: monotonic sequence number the gateway stamps on
   * every chat.* event so a reconnecting WS can drop replay events
   * that pre-date the last seen seq on the receiver side. Optional
   * because tests, the legacy RPC adapter, and the system-prompt
   * prewarm path don't always populate it.
   */
  seq?: number;
  state: "delta" | "finalizing" | "final" | "aborted" | "error" | "progress";
  /** Canonical JSONL row id when the final was persisted before broadcast. */
  transcriptId?: string;
  message?: unknown;
  progress?: {
    label?: string;
    detail?: string;
    toolName?: string;
    status?: string;
    /**
     * Optional raw tool arguments so the activity pill can render a
     * contextually specific phrase ("Reading PDF…", "Searching for
     * “TODO…”…", …). The gateway may emit this
     * alongside `toolName` when it has the full tool-call payload
     * available; absence falls back to the bare tool-name phrase.
     */
    args?: unknown;
  };
  errorMessage?: string;
  errorCode?: string;
  retryable?: boolean;
  retryAfterMs?: number;
};

/**
 * JAISIU-3119 — pick the wire-level `type` for a `ChatAttachment`
 * that the WS `chat.send` payload expects. Audio MIME types route
 * through `audios: AudioContent[]` on the server
 * (`parseMessageWithAttachments` in chat-attachments.ts); everything
 * else with an `attachmentId` is a document (the server reads bytes
 * back, runs `extractDocument`, and inlines the text into the user
 * message).
 *
 * Exported so the dispatch logic in `sendChatMessage` and any future
 * caller (e.g. a future chat composer) can stay in lockstep.
 */
export function attachmentWireType(att: ChatAttachment): "audio" | "document" | "video" {
  // JAISIU-types-sweep: `mimeType` is optional on the type — the
  // upload path sets it the moment the file is selected; missing
  // values fall through to "document" below so an upload missing
  // its declared MIME never produces a wrong wire type.
  const mime = att.mimeType ?? "";
  if (mime.startsWith("audio/")) return "audio";
  // The wire `type` is a hint for legacy routing only. Session files are
  // resolved by id server-side regardless of it, so an unrecognised family
  // is reported honestly rather than mislabelled as a document.
  if (mime.startsWith("video/")) return "video";
  return "document";
}

/**
 * JAISIU-3119 — decide whether a `ChatAttachment` should produce an
 * optimistic image content block in the user message. Only true
 * images (mime starts with `image/`) with a non-empty `dataUrl` and
 * no `error` flag qualify. Non-images and failed uploads MUST be
 * skipped here — the apiAttachments array carries the canonical
 * document / audio reference, and pushing a malformed `image` block
 * with `data: undefined` pollutes the optimistic user message.
 */
export function shouldEmitOptimisticImageBlock(att: ChatAttachment): boolean {
  if (att.error) return false;
  const mime = att.mimeType ?? "";
  if (!mime.startsWith("image/")) return false;
  if (!att.dataUrl) return false;
  return true;
}

/**
 * JAISIU-3123 — Append a `runId` to the optimistic user-message pending
 * list, deduplicating against any prior entry with the same value. The
 * list grows monotonically (entries are dropped only after
 * `mergeWithPendingUserMessages` confirms the id via `chat.history`).
 * Returns the original list reference when no change is needed so
 * Lit's reactivity isn't unnecessarily invalidated.
 */
function appendPendingUserMessageId(current: string[] | undefined, id: string): string[] {
  if (Array.isArray(current) && current.includes(id)) {
    return current;
  }
  return [...(Array.isArray(current) ? current : []), id];
}

export async function injectChatMessage(
  state: ChatState,
  message: string,
  label?: string,
  options?: { sessionKey?: string },
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  const sessionKey = options?.sessionKey?.trim() || state.sessionKey.trim();
  if (!sessionKey) {
    return false;
  }
  try {
    await state.client.request("chat.inject", {
      sessionKey,
      message,
      label,
    });
    if (sessionKey === state.sessionKey.trim()) {
      await loadChatHistory(state);
    }
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

/**
 * Drain every older `chat.history` page until the server reports the
 * beginning of the transcript. Used after a fresh reload so tab switches
 * and manual refresh paint the full thread, not just the latest window.
 */
export async function drainOlderChatHistoryPages(state: ChatState): Promise<void> {
  const HARD_CAP = 10_000;
  let safety = 0;
  while (state.chatHistoryHasMoreOlder && !state.chatHistoryLoadingOlder && safety < HARD_CAP) {
    safety += 1;
    const cursor = state.chatHistoryOldestLoadedId;
    if (!cursor || !state.client || !state.connected) {
      break;
    }
    const requestedSessionKey = state.sessionKey.trim();
    state.chatHistoryLoadingOlder = true;
    try {
      const res = await state.client.request<{
        messages?: Array<unknown>;
        oldestMessageId?: string;
        hasMoreOlder?: boolean;
      }>("chat.history", {
        sessionKey: requestedSessionKey,
        limit: Math.min(Math.max(state.chatHistoryDisplayLimit ?? 200, 50), 1000),
        before: cursor,
      });
      if (state.sessionKey.trim() !== requestedSessionKey) {
        return;
      }
      const incoming = Array.isArray(res.messages) ? res.messages : [];
      if (incoming.length === 0) {
        state.chatHistoryHasMoreOlder = res.hasMoreOlder === true;
        state.chatHistoryOldestLoadedId = res.oldestMessageId ?? null;
        continue;
      }
      const existing = Array.isArray(state.chatMessages) ? state.chatMessages : [];
      const existingIds = new Set(
        existing.map((m) => getMessageId(m)).filter((id): id is string => Boolean(id)),
      );
      const uniqueOlder = incoming.filter((m) => {
        const id = getMessageId(m);
        return !id || !existingIds.has(id);
      });
      state.chatMessages = [...uniqueOlder, ...existing];
      writeChatMessagesForSession(
        state.chatMessagesBySession,
        requestedSessionKey,
        state.chatMessages,
      );
      state.chatHistoryOldestLoadedId = res.oldestMessageId ?? null;
      state.chatHistoryHasMoreOlder = res.hasMoreOlder === true;
    } catch {
      break;
    } finally {
      state.chatHistoryLoadingOlder = false;
    }
  }
}

export async function loadChatHistory(
  state: ChatState,
  opts?: { quiet?: boolean; force?: boolean; around?: string; fullHistory?: boolean },
) {
  if (!state.client || !state.connected) {
    return;
  }
  const quiet = opts?.quiet === true;
  const force = opts?.force === true;
  const requestedSessionKey = state.sessionKey.trim();
  if (quiet) {
    if (!force && (state.chatHistoryPolling || state.chatLoading)) {
      return;
    }
    state.chatHistoryPolling = true;
  } else {
    if (state.chatLoading && !force) {
      return;
    }
    state.chatLoading = true;
    state.lastError = null;
  }
  const generation = (state.chatHistoryGeneration = (state.chatHistoryGeneration ?? 0) + 1);
  // JAISIU-3307 — wire an AbortController so the host can cancel the
  // mutation branch of an in-flight chat.history on session switch.
  // The WebSocket layer has no per-frame abort hook, so the network
  // request itself still resolves; we check `controller.signal.aborted`
  // after the `await` and skip applying the response. The generation
  // guard below is the safety-net for callers that don't keep the
  // controller around.
  const previousAbort = state.chatHistoryAbort ?? null;
  const controller = new AbortController();
  state.chatHistoryAbort = controller;
  if (previousAbort) {
    previousAbort.abort();
  }
  try {
    const limit = Math.min(Math.max(state.chatHistoryDisplayLimit ?? 200, 50), 1000);
    // JAISIU-3473 — initial page is always "latest" (no `before`). When
    // the operator scrolls up and the app calls `loadOlderChatHistory`,
    // that path issues a SECOND `chat.history` request WITH `before`
    // and prepends the result — it does NOT go through `loadChatHistory`.
    // JAISIU-3533 — `opts.around=<messageId>` issues a centered-window
    // request instead of a latest-N request. Used by the scroll-restore
    // path to land the anchor message in the DOM in a single round-trip
    // instead of paging through the cursor. Mutually exclusive with the
    // `before` cursor (which the cursor pager owns exclusively).
    const around =
      typeof opts?.around === "string" && opts.around.trim() ? opts.around.trim() : null;
    const res = await state.client.request<{
      messages?: Array<unknown>;
      thinkingLevel?: string;
      oldestMessageId?: string;
      hasMoreOlder?: boolean;
      activeClientRunId?: string;
    }>("chat.history", {
      sessionKey: requestedSessionKey,
      limit,
      ...(around ? { around } : {}),
    });
    const serverMessages = Array.isArray(res.messages) ? res.messages : [];
    const stillActive = () =>
      state.sessionKey.trim() === requestedSessionKey &&
      state.chatHistoryGeneration === generation &&
      !controller.signal.aborted;
    const live = resolveLiveMessagesForSessionKey(state, requestedSessionKey);
    // Reconcile optimistic user rows, then keep any live assistant tail the
    // server has not persisted yet (Ask-reply / final-event race).
    const merged = retainUnpersistedLiveTail(
      mergeWithPendingUserMessages(serverMessages, live),
      live,
    );
    writeChatMessagesForSession(state.chatMessagesBySession, requestedSessionKey, merged);
    pruneAndWritePendingIdsForSession(state, requestedSessionKey, serverMessages, merged);
    if (!stillActive()) {
      return;
    }
    // JAISIU-3473 — pin the upward-pagination cursor to the page we
    // just loaded. When `before` was not used (this branch), the server
    // returns the LATEST `limit` messages and tells us whether the
    // beginning is in sight via `hasMoreOlder`. The cursor is
    // authoritative for the next `loadOlderChatHistory` call.
    //
    // JAISIU-3533 — when this call was an `around=<id>` centered
    // fetch, preserve the existing cursor so the cursor pager can
    // still walk older pages if the centered window did not contain
    // the anchor (server-side, `around` may return a slice that
    // doesn't include the anchor itself). A null `oldestMessageId` on
    // a centered fetch is ambiguous; keep the previous cursor so the
    // pager has somewhere to continue from.
    if (around) {
      if (typeof res.oldestMessageId === "string" && res.oldestMessageId.length > 0) {
        state.chatHistoryOldestLoadedId = res.oldestMessageId;
      }
      state.chatHistoryHasMoreOlder = res.hasMoreOlder === true;
    } else {
      state.chatHistoryOldestLoadedId = res.oldestMessageId ?? null;
      state.chatHistoryHasMoreOlder = res.hasMoreOlder === true;
    }
    state.chatHistoryLoadingOlder = false;
    state.chatThinkingLevel = res.thinkingLevel ?? null;
    state.chatMessages = merged;
    // Session uploads are a separate store from both the transcript and the
    // project diffs, so they need their own load. Fire-and-forget: a slow or
    // failing uploads list must not delay the transcript. Skipped on the
    // quiet poll path so an idle tick doesn't re-fetch the list every cycle.
    if (!quiet && !opts?.around) {
      void loadSessionUploadsIntoState(state, requestedSessionKey);
    }
    syncChatRunFromServerHistory(state, { activeClientRunId: res.activeClientRunId });
    if (opts?.fullHistory === true && stillActive()) {
      await drainOlderChatHistoryPages(state);
    }
    const transcriptError = findLastAssistantChatError(state.chatMessages);
    if (transcriptError) {
      state.lastError = formatStructuredChatErrorPlain({ message: transcriptError });
    }
    // Fleet Control staleness verification — stamp that this session
    // just painted from the server. Inside the guarded success branch
    // so a guarded-out result (session switch / RPC racing) does not
    // falsely appear fresh.
    state.chatSync = {
      lastSyncedAt: Date.now(),
      lastEventAt: state.chatSync?.lastEventAt ?? null,
      lastWsCloseAt: state.chatSync?.lastWsCloseAt ?? null,
    };
  } catch (err) {
    // JAISIU-XXXX — quiet polls were swallowing errors entirely. The
    // operator sees a stuck transcript with no feedback, which is the
    // worst possible UX during a flaky gateway. Surface the error
    // regardless of `quiet` — `quiet` only means "skip the loading
    // overlay," not "hide problems." Still gate by session/generation
    // to avoid painting errors onto a session we already left.
    if (
      state.sessionKey.trim() === requestedSessionKey &&
      state.chatHistoryGeneration === generation &&
      !controller.signal.aborted
    ) {
      state.lastError = String(err);
    }
  } finally {
    // Release the abort slot if this is still the active load. A
    // generation bump in the meantime means the host already owns a
    // newer controller and `controller` is no longer the authoritative
    // handle — leaving it referenced would let a future caller
    // accidentally abort the next request.
    if (state.chatHistoryAbort === controller) {
      state.chatHistoryAbort = null;
    }
    if (quiet) {
      // Always release. A newer full load owns `chatLoading`, not this flag.
      // Leaving it set after a generation bump froze quiet polls until refresh.
      state.chatHistoryPolling = false;
    } else if (state.chatHistoryGeneration === generation) {
      state.chatLoading = false;
    }
  }
}

/**
 * Refresh the per-session transcript cache without painting the active
 * view. Used when a `chat.final` lands for a background/open tab while
 * the operator is viewing a different session.
 */
export async function refreshChatMessagesCacheForSession(
  state: ChatState,
  sessionKey: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const requestedSessionKey = sessionKey.trim();
  if (!requestedSessionKey) {
    return;
  }
  const limit = Math.min(Math.max(state.chatHistoryDisplayLimit ?? 200, 50), 1000);
  try {
    const res = await state.client.request<{
      messages?: Array<unknown>;
    }>("chat.history", {
      sessionKey: requestedSessionKey,
      limit,
    });
    const serverMessages = Array.isArray(res.messages) ? res.messages : [];
    const live = resolveLiveMessagesForSessionKey(state, requestedSessionKey);
    const merged = retainUnpersistedLiveTail(
      mergeWithPendingUserMessages(serverMessages, live),
      live,
    );
    writeChatMessagesForSession(state.chatMessagesBySession, requestedSessionKey, merged);
    pruneAndWritePendingIdsForSession(state, requestedSessionKey, serverMessages, merged);
  } catch {
    // Best-effort cache warm — the operator is on another tab.
  }
}

function dataUrlToBase64(dataUrl: string): { content: string; mimeType: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) {
    return null;
  }
  return { mimeType: match[1], content: match[2] };
}

/**
 * Extract a stable `id` from a chat-history message. Used to detect when
 * the server has caught up with an optimistic user message so we can drop
 * the optimistic entry and let the canonical message take its place.
 */
function getMessageId(message: unknown): string | undefined {
  if (!message || typeof message !== "object") {
    return undefined;
  }
  const record = message as { id?: unknown; transcriptId?: unknown };
  for (const candidate of [record.id, record.transcriptId]) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  const inner = unwrapHistoryMessage(message);
  if (inner && inner !== record) {
    const nested = inner.id;
    if (typeof nested === "string" && nested.trim()) {
      return nested.trim();
    }
  }
  return undefined;
}

function isOptimisticUserMessage(message: unknown): boolean {
  if (!message || typeof message !== "object") {
    return false;
  }
  const record = message as { role?: unknown; pending?: unknown };
  if (record.role !== "user") {
    return false;
  }
  return record.pending === true;
}

/**
 * Reconcile `chat.history` server messages with any optimistic user messages
 * that are still pending server confirmation. When a server user turn matches
 * a pending runId (or is a Pi prompt echo of that turn), the server copy is
 * dropped and the optimistic is promoted (`pending` cleared) so composer
 * whitespace/formatting survives. Unconfirmed optimistics stay at the tail.
 *
 * Belt-and-braces fallback: when a pending optimistic's id does NOT appear
 * in the server payload (older gateway, orphan Pi echo before id rewrite) we
 * still confirm it if the server payload contains a user message with the
 * same normalized text within CONTENT_DEDUPE_WINDOW_MS.
 */
const CONTENT_DEDUPE_WINDOW_MS = 60_000;

/**
 * Unwrap a `chat.history` envelope so we operate on the actual message
 * shape. The wire format is `{ type: "message", id, timestamp, message: {...} }`;
 * optimistic UI messages are stored flat (`{ id, role, content, timestamp }`).
 * This helper returns the inner `message` object when the envelope is
 * recognised, otherwise the input itself — so both shapes are accepted.
 */
function unwrapHistoryMessage(message: unknown): Record<string, unknown> | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const candidate = message as { type?: unknown; message?: unknown };
  if (candidate.type === "message" && candidate.message && typeof candidate.message === "object") {
    return candidate.message as Record<string, unknown>;
  }
  return candidate as Record<string, unknown>;
}

function extractUserText(message: unknown): string {
  const inner = unwrapHistoryMessage(message);
  if (!inner) {
    return "";
  }
  const content = inner.content;
  if (typeof content === "string") {
    return content.trim();
  }
  if (!Array.isArray(content)) {
    return "";
  }
  const parts: string[] = [];
  for (const block of content) {
    if (!block || typeof block !== "object") {
      continue;
    }
    const type = (block as { type?: unknown }).type;
    if (type === "text") {
      const text = (block as { text?: unknown }).text;
      if (typeof text === "string") {
        parts.push(text);
      }
    }
  }
  return parts.join("\n").trim();
}

const MESSAGE_ID_HINT_RE = /\[message_id:\s*([^\]]+)\]/i;
const ENVELOPE_PREFIX_RE = /^\[([^\]]+)\]\s*/;

function extractMessageIdHint(message: unknown): string | undefined {
  const text = extractUserText(message);
  if (!text) {
    return undefined;
  }
  const match = MESSAGE_ID_HINT_RE.exec(text);
  const id = match?.[1]?.trim();
  return id && id.length > 0 ? id : undefined;
}

function stripEnvelopeHeader(text: string): string {
  const match = text.match(ENVELOPE_PREFIX_RE);
  if (!match) {
    return text;
  }
  const header = match[1] ?? "";
  if (
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z\b/.test(header) ||
    /\d{4}-\d{2}-\d{2} \d{2}:\d{2}\b/.test(header)
  ) {
    return text.slice(match[0].length);
  }
  return text;
}

/**
 * Normalize user text for echo/optimistic matching: drop `[message_id:]`,
 * inbound envelope headers, and collapse whitespace so the Pi prompt copy
 * compares equal to the composer text the user actually typed.
 */
function normalizeUserTextForDedupe(message: unknown): string {
  let text = extractUserText(message);
  if (!text) {
    return "";
  }
  text = text.replace(/\[message_id:\s*[^\]]+\]/gi, "");
  text = stripEnvelopeHeader(text);
  return text.replace(/\s+/g, " ").trim();
}

function isPromptEchoUserMessage(message: unknown): boolean {
  const inner = unwrapHistoryMessage(message);
  if (!inner || inner.role !== "user") {
    return false;
  }
  return Boolean(extractMessageIdHint(message));
}

function clearOptimisticPending(message: unknown): unknown {
  if (!message || typeof message !== "object") {
    return message;
  }
  // Drop both `pending` (the wire-level "not yet canonical") and `queued`
  // (the collect-mode "waiting behind an in-flight run") flags. They have
  // different lifecycles: `pending` is cleared on server reconciliation,
  // `queued` is cleared the same moment because once the server echoes the
  // message back the operator can already see it in the transcript — the
  // chip would only add visual noise.
  const { pending: _pending, queued: _queued, ...rest } = message as Record<string, unknown>;
  void _pending;
  void _queued;
  return rest;
}

/**
 * Drop Pi `session.prompt()` user echoes from a history payload.
 * Returns the filtered list plus any `[message_id:]` refs that confirmed a
 * known local/pending user id (gateway row may be missing from the transcript).
 */
export function filterPromptEchoUserMessages(
  serverMessages: unknown[],
  knownUserIds: ReadonlySet<string>,
  knownNormalizedTexts: ReadonlySet<string>,
): { messages: unknown[]; confirmedIds: Set<string> } {
  const confirmedIds = new Set<string>();
  const messages: unknown[] = [];
  for (const message of serverMessages) {
    const inner = unwrapHistoryMessage(message);
    if (!inner || inner.role !== "user" || !isPromptEchoUserMessage(message)) {
      messages.push(message);
      continue;
    }
    const hint = extractMessageIdHint(message);
    const norm = normalizeUserTextForDedupe(message);
    const matchesId = Boolean(hint && knownUserIds.has(hint));
    const matchesText = Boolean(norm && knownNormalizedTexts.has(norm));
    if (matchesId || matchesText) {
      if (hint) {
        confirmedIds.add(hint);
      }
      continue;
    }
    messages.push(message);
  }
  return { messages, confirmedIds };
}

function extractMessageTimestamp(message: unknown): number | undefined {
  const inner = unwrapHistoryMessage(message);
  if (!inner) {
    return undefined;
  }
  const candidate = inner.timestamp;
  if (typeof candidate === "number" && Number.isFinite(candidate)) {
    return candidate;
  }
  if (typeof candidate === "string") {
    const parsed = Date.parse(candidate);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

export function pruneConfirmedPendingUserMessageIds(
  state: ChatState,
  serverMessages: unknown[],
  mergedMessages: unknown[],
): void {
  if (!Array.isArray(state.pendingUserMessageIds) || state.pendingUserMessageIds.length === 0) {
    return;
  }
  const serverIds = new Set<string>();
  for (const message of serverMessages) {
    const id = getMessageId(message);
    if (id) {
      serverIds.add(id);
    }
  }
  const mergedIds = new Set<string>();
  for (const message of mergedMessages) {
    if (isOptimisticUserMessage(message)) {
      continue;
    }
    const id = getMessageId(message);
    if (id) {
      mergedIds.add(id);
    }
  }
  const confirmed = new Set([...serverIds, ...mergedIds]);
  state.pendingUserMessageIds = state.pendingUserMessageIds.filter((id) => !confirmed.has(id));
}

function appendPendingUserMessageIdForSession(
  state: ChatState,
  sessionKey: string,
  runId: string,
): string[] {
  const key = sessionKey.trim();
  const current =
    state.sessionKey.trim() === key
      ? (state.pendingUserMessageIds ?? [])
      : readPendingUserMessageIdsForSession(state.pendingUserMessageIdsBySession, key);
  const next = appendPendingUserMessageId(current, runId);
  writePendingUserMessageIdsForSession(state.pendingUserMessageIdsBySession, key, next);
  paintPendingUserMessageIdsIfActive(state, key, next);
  return next;
}

function setChatMessagesForSession(
  state: ChatState,
  sessionKey: string,
  messages: readonly unknown[],
): void {
  writeChatMessagesForSession(state.chatMessagesBySession, sessionKey, messages);
  paintChatMessagesIfActive(state, sessionKey, [...messages]);
}

function patchChatMessagesForBoundSession(
  state: ChatState,
  sessionKey: string,
  patch: (messages: readonly unknown[]) => unknown[],
): unknown[] {
  const key = sessionKey.trim();
  if (state.sessionKey.trim() === key) {
    const next = patch(state.chatMessages ?? []);
    setChatMessagesForSession(state, key, next);
    return next;
  }
  return patchChatMessagesForSession(state.chatMessagesBySession, key, patch);
}

function pruneAndWritePendingIdsForSession(
  state: ChatState,
  sessionKey: string,
  serverMessages: unknown[],
  mergedMessages: unknown[],
): void {
  const key = sessionKey.trim();
  const pending =
    state.sessionKey.trim() === key
      ? [...(state.pendingUserMessageIds ?? [])]
      : readPendingUserMessageIdsForSession(state.pendingUserMessageIdsBySession, key);
  const scratch: ChatState = { ...state, pendingUserMessageIds: pending };
  pruneConfirmedPendingUserMessageIds(scratch, serverMessages, mergedMessages);
  writePendingUserMessageIdsForSession(
    state.pendingUserMessageIdsBySession,
    key,
    scratch.pendingUserMessageIds ?? [],
  );
  paintPendingUserMessageIdsIfActive(state, key, scratch.pendingUserMessageIds ?? []);
}

export function mergeWithPendingUserMessages(
  serverMessages: unknown[],
  currentMessages: unknown[],
): unknown[] {
  const pendingIds = new Set<string>();
  const pendingOptimistic: unknown[] = [];
  const knownUserIds = new Set<string>();
  const knownNormalizedTexts = new Set<string>();
  for (const message of currentMessages) {
    const id = getMessageId(message);
    const inner = unwrapHistoryMessage(message);
    if (inner?.role === "user") {
      if (id) {
        knownUserIds.add(id);
      }
      const norm = normalizeUserTextForDedupe(message);
      if (norm) {
        knownNormalizedTexts.add(norm);
      }
    }
    if (!isOptimisticUserMessage(message)) {
      continue;
    }
    if (id) {
      pendingIds.add(id);
    }
    pendingOptimistic.push(message);
  }

  // Build a stable-id index of the *non-pending* portion of the local
  // chat. When the gateway returns the same canonical user message on a
  // subsequent `chat.history` poll — or the WS reconnects and replays it —
  // we must not append a second copy just because the optimistic was
  // already reconciled on an earlier pass. Without this guard the same
  // user message could render twice (the bug fixed in this commit).
  // NOTE: optimistics are intentionally excluded from this set; their
  // ids are already represented in `pendingIds`.
  const existingIds = new Set<string>();
  for (const message of currentMessages) {
    if (isOptimisticUserMessage(message)) {
      continue;
    }
    const id = getMessageId(message);
    if (id) {
      existingIds.add(id);
    }
  }

  // Drop any duplicate canonical messages inside the server payload itself
  // (first occurrence wins). This is independent of optimistic reconciliation
  // and protects against gateway-side replays that ship the same id twice
  // in a single response.
  const dedupedServerMessages: unknown[] = [];
  const seenServerIds = new Set<string>();
  for (const message of serverMessages) {
    const id = getMessageId(message);
    if (id) {
      if (seenServerIds.has(id)) {
        continue;
      }
      seenServerIds.add(id);
    }
    dedupedServerMessages.push(message);
  }

  // Drop Pi prompt echoes that still carry `[message_id:]` (pre-sanitize
  // payloads / older gateways). Confirmed ids are treated like id matches.
  const { messages: withoutPromptEchoes, confirmedIds: echoConfirmedIds } =
    filterPromptEchoUserMessages(dedupedServerMessages, knownUserIds, knownNormalizedTexts);

  if (pendingOptimistic.length === 0) {
    // No optimistics in play — the merge is purely about reconciling
    // server history against already-reconciled local entries. Preserve
    // any local non-pending entry whose id the server has now re-anchored
    // to a different timestamp, so the user doesn't see the displayed
    // timestamp jump on every refresh.
    if (existingIds.size === 0) {
      return withoutPromptEchoes;
    }
    const filteredServer: unknown[] = [];
    for (const message of withoutPromptEchoes) {
      const id = getMessageId(message);
      if (id && existingIds.has(id)) {
        continue;
      }
      filteredServer.push(message);
    }
    // Local canonical entries first (preserving their original timestamps),
    // then any new server messages that weren't already in the local chat.
    return currentMessages.filter((m) => !isOptimisticUserMessage(m)).concat(filteredServer);
  }

  // Confirm pending optimistics by id (including gateway rewrite of orphan
  // Pi echoes onto the client runId) or by normalized text within the
  // content window. Prefer the optimistic bubble so composer formatting /
  // whitespace survives — the history copy is often a stripped Pi echo.
  const confirmedPendingIds = new Set<string>(echoConfirmedIds);
  const dropServerIds = new Set<string>(echoConfirmedIds);
  const dropServerMessages = new Set<unknown>();

  for (const message of withoutPromptEchoes) {
    const id = getMessageId(message);
    if (id && pendingIds.has(id)) {
      confirmedPendingIds.add(id);
      dropServerIds.add(id);
      dropServerMessages.add(message);
    }
  }

  const pendingByNorm = new Map<string, unknown[]>();
  for (const optimistic of pendingOptimistic) {
    const norm = normalizeUserTextForDedupe(optimistic);
    if (!norm) {
      continue;
    }
    const bucket = pendingByNorm.get(norm) ?? [];
    bucket.push(optimistic);
    pendingByNorm.set(norm, bucket);
  }

  for (const message of withoutPromptEchoes) {
    if (dropServerMessages.has(message)) {
      continue;
    }
    const inner = unwrapHistoryMessage(message);
    if (!inner || inner.role !== "user") {
      continue;
    }
    const id = getMessageId(message);
    if (id && (pendingIds.has(id) || existingIds.has(id))) {
      continue;
    }
    const norm = normalizeUserTextForDedupe(message);
    if (!norm) {
      continue;
    }
    const bucket = pendingByNorm.get(norm);
    if (!bucket || bucket.length === 0) {
      continue;
    }
    const optimistic = bucket[0];
    const optimisticTs = extractMessageTimestamp(optimistic);
    const candidateTs = extractMessageTimestamp(message);
    if (optimisticTs !== undefined && candidateTs !== undefined) {
      if (Math.abs(optimisticTs - candidateTs) > CONTENT_DEDUPE_WINDOW_MS) {
        continue;
      }
    }
    bucket.shift();
    const optimisticId = getMessageId(optimistic);
    if (optimisticId) {
      confirmedPendingIds.add(optimisticId);
    }
    dropServerMessages.add(message);
  }

  const filteredServer = withoutPromptEchoes.filter((message) => {
    if (dropServerMessages.has(message)) {
      return false;
    }
    const id = getMessageId(message);
    if (id && dropServerIds.has(id)) {
      return false;
    }
    return true;
  });

  const reconciledPending: unknown[] = [];
  for (const optimistic of pendingOptimistic) {
    const id = getMessageId(optimistic);
    if (id && confirmedPendingIds.has(id)) {
      reconciledPending.push(clearOptimisticPending(optimistic));
      continue;
    }
    if (id && existingIds.has(id)) {
      continue;
    }
    reconciledPending.push(optimistic);
  }

  // Mirror the no-pending branch: keep local canonical rows the server
  // snapshot has not caught up with, then append still-pending optimistics.
  const localNonPending = currentMessages.filter((m) => !isOptimisticUserMessage(m));
  const filteredNewFromServer = filteredServer.filter((message) => {
    const id = getMessageId(message);
    return !(id && existingIds.has(id));
  });
  const reconciledIds = new Set<string>();
  for (const message of reconciledPending) {
    const id = getMessageId(message);
    if (id) {
      reconciledIds.add(id);
    }
  }
  const localWithoutReconciledDupes = localNonPending.filter((message) => {
    const id = getMessageId(message);
    return !(id && reconciledIds.has(id));
  });
  // JAISIU-XXXX — a local non-pending row is only legitimately "not yet
  // persisted" when it belongs to the chat the server is describing. The
  // optimistic user in flight is the newest anchor for this chat, so a
  // local row sitting before it is only trustworthy when the server
  // snapshot proves continuity with it:
  //   * the row is confirmed by id or by role+text fingerprint (the common
  //     case — the server simply has not re-sent it yet), or
  //   * the snapshot is otherwise non-empty and shares at least one
  //     fingerprint with the local rows before the anchor, which means the
  //     snapshot is lagging on the SAME chat rather than describing a
  //     different one. This second clause preserves the unmatched prior
  //     assistant reply from the JAISIU-XXXX monologue regression, where
  //     the server-side filter drops assistant rows the local chat holds.
  // When neither holds, the local rows are residue from a previously-open
  // chat and must not be carried across. Rows newer than the anchor are
  // always kept: that is the in-flight reply the server has not persisted.
  const newestOptimisticIdx = currentMessages.reduce<number>(
    (acc, message, idx) => (isOptimisticUserMessage(message) ? idx : acc),
    -1,
  );
  let localRows = localWithoutReconciledDupes;
  if (newestOptimisticIdx > 0) {
    const serverFingerprints = new Set<string>();
    for (const message of serverMessages) {
      const id = getMessageId(message);
      if (id) {
        serverFingerprints.add(`id:${id}`);
      }
      const roleText = roleTextFingerprint(message);
      if (roleText) {
        serverFingerprints.add(roleText);
      }
    }
    const isConfirmed = (message: unknown): boolean => {
      const id = getMessageId(message);
      if (id && serverFingerprints.has(`id:${id}`)) {
        return true;
      }
      const roleText = roleTextFingerprint(message);
      return Boolean(roleText && serverFingerprints.has(roleText));
    };
    // Any pre-anchor local row the server confirms proves the snapshot and
    // the local chat are the same conversation, which licenses keeping the
    // sibling rows it has not echoed back yet.
    const snapshotOverlapsLocalChat = currentMessages
      .slice(0, newestOptimisticIdx)
      .some((message) => !isOptimisticUserMessage(message) && isConfirmed(message));
    localRows = currentMessages.reduce<unknown[]>((acc, message, idx) => {
      if (isOptimisticUserMessage(message)) {
        return acc;
      }
      const id = getMessageId(message);
      if (id && reconciledIds.has(id)) {
        return acc;
      }
      if (idx > newestOptimisticIdx || snapshotOverlapsLocalChat || isConfirmed(message)) {
        acc.push(message);
      }
      return acc;
    }, []);
  }
  return localRows.concat(filteredNewFromServer, reconciledPending);
}

/**
 * Shape required to append a user message via `appendOptimisticUserMessage`.
 * Kept narrow so the helper can be reused by the send, steer, and inject
 * code paths.
 */
export type ChatMessageAppendInput = {
  id: string;
  role: string;
  content: unknown;
  timestamp: number;
  pending?: boolean;
};

/**
 * Append an optimistic or canonical user message to a chat list, refusing
 * to insert a second entry with the same stable id. Every user message
 * generated by this UI MUST flow through this helper so the dedupe
 * contract holds across `chat.send`, `chat.steer`, `chat.inject`, and
 * WS `chat.final` reconciliation. Returns the original list reference
 * unchanged when nothing was appended (idempotent for retries, replays,
 * and reconnect-triggered re-sends).
 */
export function appendOptimisticUserMessage(
  current: readonly unknown[],
  input: ChatMessageAppendInput,
): unknown[] {
  if (!input || typeof input !== "object") {
    return current as unknown[];
  }
  const id = typeof input.id === "string" ? input.id.trim() : "";
  if (!id) {
    // Without a stable id we have no way to dedupe, so we refuse to
    // append rather than risk an invisible duplicate. The caller should
    // always generate an id (runId, steerId, injectId).
    return current as unknown[];
  }
  for (const existing of current) {
    if (!existing || typeof existing !== "object") {
      continue;
    }
    const existingId = getMessageId(existing);
    if (existingId && existingId === id) {
      // Already present (pending or canonical). Don't append a second copy
      // and don't replace the existing entry — preserves the timestamp of
      // the optimistic the user just sent.
      return current as unknown[];
    }
  }
  return [...current, input];
}

function roleTextFingerprint(message: unknown): string | undefined {
  const inner = unwrapHistoryMessage(message);
  if (!inner) {
    return undefined;
  }
  const role = typeof inner.role === "string" ? inner.role : "";
  const text = (extractText(inner) ?? extractUserText(message)).trim();
  if (!role || !text) {
    return undefined;
  }
  return `role:${role}|text:${text}`;
}

/**
 * JAISIU-3558 — shape of the streaming assistant-tail entry that lives
 * inside `state.chatMessages` between the first `chat.delta` and the
 * matching `chat.final`. Holds the latest text the agent has emitted so
 * the transcript (the single source of truth for what the operator has
 * seen) reflects the in-progress reply without waiting for the final
 * message. The `id` is the active `runId` so the view's existing
 * `messageKey()` returns a stable Lit key across re-renders.
 */
import {
  dropStreamingTail,
  extendStreamingTail,
  findStreamingTailIndex,
  isStreamingTranscriptTail,
  seedStreamingTailFromLiveFields,
  type StreamingTranscriptTail,
} from "../chat/chat-streaming-tail.ts";
export type { StreamingTranscriptTail } from "../chat/chat-streaming-tail.ts";
export {
  dropStreamingTail,
  extendStreamingTail,
  findStreamingTailIndex,
  isStreamingTranscriptTail,
  seedStreamingTailFromLiveFields,
} from "../chat/chat-streaming-tail.ts";
import {
  bootstrapLiveRunFromHistory,
  reconcileStaleChatRunFromHistory,
  syncChatRunFromServerHistory,
} from "../chat/chat-run-sync.ts";
export {
  bootstrapLiveRunFromHistory,
  reconcileStaleChatRunFromHistory,
  syncChatRunFromServerHistory,
} from "../chat/chat-run-sync.ts";

/**
 * JAISIU-3558 — collapse the streaming tail inside `chatMessages` into
 * the canonical assistant final message that just landed, in place.
 * Behaviour:
 *
 *   - if a streaming tail exists for `runId`, it is removed and the
 *     canonical final message is appended at the same position. The view
 *     does not see a brand-new row pop in at the bottom; the tail simply
 *     transforms into the real message.
 *   - if no tail exists for `runId`, the canonical final is appended at
 *     the tail of the list (the pre-3558 behaviour).
 *   - the "final already present" idempotency check (by id + role, or by
 *     text-match within the current turn) keeps replays / WS reconnects
 *     from double-appending the canonical message. The streaming tail is
 *     never a match candidate: it carries the run id and the streamed
 *     text, so matching it would drop the reply it is about to become.
 *
 * Returns the next array reference (may be the SAME as the input when the
 * canonical message was already present or when the input was empty).
 */
export function collapseStreamingTailIntoFinal(
  messages: readonly unknown[],
  args: {
    runId: string | null | undefined;
    finalMessage: unknown;
    /** Optional already-computed id of `finalMessage` — callers that
     *  have already stamped the runId onto a missing-id message pass the
     *  resulting id here so we do not recompute it. */
    finalId?: string;
    /** Optional already-computed text of `finalMessage`. */
    finalText?: string;
  },
): unknown[] {
  if (!args || typeof args !== "object" || !args.finalMessage) {
    return messages as unknown[];
  }
  const list = Array.isArray(messages) ? messages : [];
  const incomingId =
    typeof args.finalId === "string" && args.finalId.trim()
      ? args.finalId.trim()
      : typeof args.finalMessage === "object"
        ? (getMessageId(args.finalMessage as unknown) ?? "")
        : "";
  const incomingText =
    typeof args.finalText === "string"
      ? args.finalText.trim()
      : (extractText(args.finalMessage) ?? "").trim();
  const currentTurnStart = lastUserMessageIndex(list) + 1;
  const alreadyPresentIdx = list.findIndex((existing, index) => {
    if (!existing || typeof existing !== "object" || isStreamingTranscriptTail(existing)) {
      return false;
    }
    if (incomingId) {
      const existingId = getMessageId(existing as unknown);
      if (existingId && existingId === incomingId) {
        // JAISIU-3558 (dedupe by id + role) — the chat.final stamping
        // path adds `id: runId` onto the assistant canonical message
        // when the gateway ships no id. If a previous chat.send stamped
        // the optimistic user row with the SAME runId (the test
        // fixture and the chat-send path both do this), a naive id-only
        // dedupe would treat the user row as the assistant final and
        // silently drop the assistant on `chat.final`. Match by id AND
        // role so the user and assistant rows with identical ids do
        // not collapse into each other.
        const existingRole = (existing as { role?: unknown }).role;
        const incomingRole = (args.finalMessage as { role?: unknown })?.role;
        if (existingRole === incomingRole) {
          return true;
        }
      }
    }
    if (!incomingText || index < currentTurnStart) {
      return false;
    }
    const existingText = (extractText(existing as unknown) ?? "").trim();
    return existingText === incomingText;
  });
  if (alreadyPresentIdx !== -1) {
    const trimmed = dropStreamingTail(list, args.runId);
    return trimmed as unknown[];
  }
  const tailIndex =
    args.runId && typeof args.runId === "string" ? findStreamingTailIndex(list, args.runId) : -1;
  if (tailIndex === -1) {
    return [...list, args.finalMessage];
  }
  const next = list.slice();
  next.splice(tailIndex, 1, args.finalMessage);
  return next;
}

function lastUserMessageIndex(messages: readonly unknown[]): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (unwrapHistoryMessage(messages[i])?.role === "user") {
      return i;
    }
  }
  return -1;
}

/**
 * Keep live transcript messages that `chat.history` has not persisted yet.
 * After an Ask reply the agent `final` event appends the follow-up locally,
 * then a racing history RPC returns the pre-follow-up snapshot and would
 * otherwise wipe it until a full page refresh.
 */
/**
 * Compute the watermark index in `liveMessages` — the smallest index `i`
 * such that `liveMessages[i]` is strictly newer (by `timestamp`) than
 * `serverLastTs`. Returns `-1` when every row in `liveMessages` is at
 * or below `serverLastTs`, or when `liveMessages` is empty.
 *
 * The watermark is the contract that lets `retainUnpersistedLiveTail`
 * decide which slice of `live` the server has NOT persisted yet. The
 * JAISIU-3558 chat-streaming tail lives past the watermark; the rows
 * at-or-before the watermark are either already in the server snapshot
 * or stale (must not bleed in).
 *
 * Used by `retainUnpersistedLiveTail(server, live, watermark)` and
 * exposed standalone so the watermark itself can be cached / replayed
 * / unit-tested in isolation.
 */
export function computeLiveTailWatermark(liveMessages: unknown[], serverLastTs: number): number {
  if (!Array.isArray(liveMessages) || liveMessages.length === 0) {
    return -1;
  }
  // JAISIU-3558 — the watermark is the first server-side message-shaped
  // row (assistant, tool, system — anything except optimistic user
  // rows) whose `timestamp` is strictly greater than `serverLastTs`.
  // User rows are skipped: their timestamps come from the client clock
  // (random / sequence-derived for tests, real-clock for production)
  // and are not a reliable "newer than server" signal. Without the
  // skip, an optimistic user bubble with a fresh timestamp would alias
  // the watermark onto an in-flight row and crash the retainLiveTail
  // contract.
  for (let i = 0; i < liveMessages.length; i += 1) {
    const m = liveMessages[i];
    if (!m || typeof m !== "object") {
      continue;
    }
    const role = (m as { role?: unknown }).role;
    if (role === "user") {
      continue;
    }
    const ts = extractMessageTimestamp(m);
    if (ts == null) {
      continue;
    }
    if (ts > serverLastTs) {
      return i;
    }
  }
  return -1;
}

export function retainUnpersistedLiveTail(
  serverMessages: unknown[],
  liveMessages: unknown[],
  /**
   * Optional pre-computed watermark (from `computeLiveTailWatermark`).
   * `-1` means "no row in live is newer than the server snapshot" and
   * matches the legacy 2-arg behaviour exactly (walk from end, anchor
   * on the first server match, drop everything before it). `>=0` means
   * `live[watermark..end]` is the live-tail slice the server has not
   * caught up with yet; the function trusts the watermark and skips the
   * end-→-start walk.
   */
  watermark?: number,
): unknown[] {
  if (!Array.isArray(liveMessages) || liveMessages.length === 0) {
    return serverMessages;
  }
  // JAISIU-3558 watermark fast-path. When the caller supplies a
  // pre-computed watermark (the canonical "first row newer than the
  // server snapshot" index), the watermark itself IS the anchor:
  // every row in `live[watermark..]` is by definition not yet
  // persisted by the server (that's what the watermark means), so we
  // simply concat. We additionally preserve any optimistic user rows
  // that sit BEFORE the watermark (chat-send in flight) so they are
  // not silently dropped when the snapshot arrives.
  if (typeof watermark === "number" && watermark >= 0) {
    const slice = liveMessages.slice(watermark);
    const head = liveMessages.slice(0, watermark);
    const serverIds = new Set<string>();
    for (const s of serverMessages) {
      const id = getMessageId(s);
      if (id) {
        serverIds.add(id);
      }
    }
    // Preserve optimistic user rows that sit BEFORE the watermark (they
    // represent an in-flight chat-send whose server echo has not landed
    // yet). Dedupe by id against `serverMessages` so we never append the
    // same optimistic row twice (the canonical "already persisted, drop
    // the pending echo" contract).
    const optimisticHead = head.filter(isOptimisticUserMessage).filter((m) => {
      const id = getMessageId(m);
      return !id || !serverIds.has(id);
    });
    if (slice.length === 0 && optimisticHead.length === 0) {
      return serverMessages;
    }
    return serverMessages.concat(optimisticHead, slice);
  }
  const serverKeys = new Set<string>();
  for (const message of serverMessages) {
    const id = getMessageId(message);
    if (id) {
      serverKeys.add(`id:${id}`);
    }
    const roleText = roleTextFingerprint(message);
    if (roleText) {
      serverKeys.add(roleText);
    }
  }
  // Walk `live` from end → start. Collect messages the server hasn't
  // caught up with yet. The walk is anchored at the first message in
  // `live` that matches the server snapshot — everything before that
  // anchor is either already persisted or stale (and must not leak across
  // chat reloads). Optimistic user messages are SKIPPED rather than used
  // as anchors, so an unmatched assistant reply sitting between an
  // optimistic user (the in-flight turn) and a canonical anchor in `live`
  // is preserved instead of being silently dropped — see JAISIU-XXXX
  // for the chat-monologue regression this prevents.
  const tail: unknown[] = [];
  let anchorFound = false;
  let optimisticUserIdx = -1;
  for (let i = liveMessages.length - 1; i >= 0; i -= 1) {
    const message = liveMessages[i];
    if (isOptimisticUserMessage(message)) {
      if (optimisticUserIdx === -1) {
        optimisticUserIdx = i;
      }
      continue;
    }
    // JAISIU-3558 — a streaming-tail entry is a synthetic assistant
    // row maintained by the host (`extendStreamingTail`); it is
    // authoritative for what the operator has SEEN but is NOT a
    // canonical transcript row. Skip it as an anchor candidate so a
    // server snapshot whose `role:text` happens to match the in-flight
    // tail text does not truncate the walk early; the next canonical
    // entry (or the optimistic user) is the correct anchor.
    if (isStreamingTranscriptTail(message)) {
      tail.unshift(message);
      continue;
    }
    const id = getMessageId(message);
    const roleText = roleTextFingerprint(message);
    if ((id && serverKeys.has(`id:${id}`)) || (roleText && serverKeys.has(roleText))) {
      anchorFound = true;
      break;
    }
    tail.unshift(message);
  }
  if (tail.length === 0) {
    return serverMessages;
  }
  // Nothing anchors the walk (no server match in `live` and no optimistic
  // user) — the live messages are stale from a different chat; discard.
  if (!anchorFound && optimisticUserIdx === -1) {
    return serverMessages;
  }
  if (anchorFound) {
    return serverMessages.concat(tail);
  }
  // Anchored only by the optimistic user (typical for instant slash replies
  // like `/help`): trust tail items that are STRICTLY NEWER than the
  // optimistic user. Anything older belongs to a different chat and must
  // not bleed in. tail[0] corresponds to live[firstTailLiveIdx].
  const firstTailLiveIdx = liveMessages.length - tail.length;
  const keepFromLiveIdx = optimisticUserIdx + 1;
  if (firstTailLiveIdx >= keepFromLiveIdx) {
    return serverMessages.concat(tail);
  }
  const dropCount = keepFromLiveIdx - firstTailLiveIdx;
  const trimmed = tail.slice(dropCount);
  if (trimmed.length === 0) {
    return serverMessages;
  }
  return serverMessages.concat(trimmed);
}

/** Newest assistant `timestamp` in a mixed live/envelope transcript. */
export function newestAssistantTimestampMs(messages: unknown[]): number | null {
  let newest: number | null = null;
  for (const message of messages) {
    const inner = unwrapHistoryMessage(message);
    if (!inner || inner.role !== "assistant") {
      continue;
    }
    const ts = extractMessageTimestamp(message);
    if (ts == null) {
      continue;
    }
    if (newest == null || ts > newest) {
      newest = ts;
    }
  }
  return newest;
}

export async function sendChatMessage(
  state: ChatState,
  message: string,
  attachments?: ChatAttachment[],
): Promise<string | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const originSessionKey = state.sessionKey.trim();
  if (!originSessionKey) {
    return null;
  }
  const msg = message.trim();
  const hasAttachments = attachments && attachments.length > 0;
  if (!msg && !hasAttachments) {
    return null;
  }

  const now = Date.now();
  const paintRunStateOnActiveSession = originSessionKey === state.sessionKey.trim();

  // Build user message content blocks
  const contentBlocks: Array<{ type: string; text?: string; source?: unknown }> = [];
  if (msg) {
    contentBlocks.push({ type: "text", text: msg });
  }
  // Add image previews to the message for display.
  // JAISIU-3119 / JAISIU-3119 (Unit 5): only push an `image` content block
  // when the attachment is actually an image. Previously the loop pushed
  // a block for every attachment, leaving non-images with
  // `data: undefined` — which the model saw as a malformed block. Failed
  // uploads (`att.error`) are skipped here too so the optimistic user
  // message never claims an attachment the server never received.
  if (hasAttachments) {
    for (const att of attachments) {
      if (!shouldEmitOptimisticImageBlock(att)) {
        continue;
      }
      contentBlocks.push({
        type: "image",
        source: { type: "base64", media_type: att.mimeType, data: att.dataUrl },
      });
    }
  }

  const runId = generateUUID();
  // Mark the optimistic message with a stable id so chat.history polls
  // can detect when the server's canonical version arrives and drop
  // the optimistic without duplicating it. The id is the same runId
  // used as the chat.send idempotencyKey on the wire.
  // Collect-mode detection: if a previous run is still active when this
  // send arrives, the new message will sit in the server-side queue
  // until the current turn closes. Mark the optimistic bubble so the
  // renderer can show a "Queued — will land when the agent is free"
  // chip instead of the plain "pending" appearance. The flag is
  // cleared on server reconciliation (see `clearOptimisticPending`)
  // OR when the agent finalizes without ever acknowledging the runId
  // (the failed-run path on line ~1045 also strips it). When no run
  // is active the message is delivered straight to the agent and the
  // chip never renders.
  const isCollecting =
    Boolean(state.chatRunId) && (state.chatSending || state.chatStreamStartedAt != null);
  // JAISIU-XXXX — pin `sessionFiles` onto the optimistic bubble so the
  // file chip (and any other `message.sessionFiles`-driven render) is
  // visible from the moment the user hits Send. `mergeWithPendingUserMessages`
  // prefers the optimistic copy on id match (to preserve composer
  // formatting / whitespace), so without this the operator only saw the
  // chips after a hard refresh — the server's canonical row carries
  // `sessionFiles`, but the optimistic copy it is reconciled against did
  // not. We already have every `sessionFileId` at compose time
  // (`chat-composer-attachments.ts` writes it onto the in-flight
  // ChatAttachment), so the optimistic can mirror the server's shape
  // without waiting for the round-trip. Files that haven't finished
  // uploading (no `sessionFileId` yet) or failed uploads are skipped
  // here too so the optimistic never claims a chip the server never
  // stored.
  const optimisticSessionFiles = hasAttachments
    ? (attachments ?? []).flatMap((att) => {
        if (!att.sessionFileId) return [];
        if (att.error) return [];
        const filename = att.filename ?? att.name ?? "file";
        const mediaType = att.mimeType ?? att.mime ?? "";
        const size = typeof att.size === "number" ? att.size : (att.sizeBytes ?? 0);
        return [
          {
            fileId: att.sessionFileId,
            filename,
            mediaType,
            size,
            kind: att.kind,
          },
        ];
      })
    : [];
  const optimisticUserMessage = {
    id: runId,
    role: "user",
    content: contentBlocks,
    timestamp: now,
    pending: true,
    queued: isCollecting,
    ...(optimisticSessionFiles.length > 0 ? { sessionFiles: optimisticSessionFiles } : {}),
  };
  // Idempotent append: a second `chat.send` with the same runId, a
  // reconnect-triggered replay, or a re-entrant call must NEVER add a
  // second copy of the same user message (the duplicate-message bug
  // fixed in this commit). See `appendOptimisticUserMessage`.
  patchChatMessagesForBoundSession(state, originSessionKey, (current) =>
    appendOptimisticUserMessage(current, optimisticUserMessage),
  );
  appendPendingUserMessageIdForSession(state, originSessionKey, runId);

  // Bump the collect-mode counter so the composer banner and per-bubble
  // chips know there are messages waiting on the in-flight run. Decremented
  // in `handleChatEvent` when a matching `final` lands, and on the failed-
  // run cleanup path so the counter never latches above zero.
  if (isCollecting && paintRunStateOnActiveSession) {
    state.chatCollectQueueDepth = (state.chatCollectQueueDepth ?? 0) + 1;
  }

  if (paintRunStateOnActiveSession) {
    state.chatSending = true;
    state.lastError = null;
    clearChatLastRunSnapshot(
      state as unknown as import("../chat/chat-last-run-files.ts").ChatLastRunFilesHost,
      originSessionKey,
    );
    state.chatRunId = runId;
    state.chatStream = "";
    state.chatReasoningStream = "";
    state.chatStreamStartedAt = now;
  }
  // JAISIU-activity-always-visible: snapshot the live fields into the
  // per-session cache the moment chat.send starts. When the operator
  // switches tabs mid-run, this snapshot lets the new tab restore the
  // "Working…" indicator synchronously instead of waiting for the
  // next delta. Subsequent event handlers update both the active
  // fields and the cache so the cache never drifts from the active
  // session's truth.
  recordLiveState(state, originSessionKey, {
    runId,
    startedAt: now,
    stream: "",
    reasoningStream: "",
    liveRunHint: state.chatLiveRunHint ?? null,
    activeFileToolPath: state.chatActiveFileToolPath ?? null,
    mainRun: state.chatMainRun ?? null,
    compaction: state.compactionStatus ?? null,
    injectedAt: null,
    // JAISIU-chat-switch-reload — pin the queue depth to the session
    // that owns the in-flight run. Reading the global counter here
    // already post-bumps (the increment at :942 runs first), so the
    // snapshot reflects the depth AFTER this send was queued.
    collectQueueDepth: paintRunStateOnActiveSession ? (state.chatCollectQueueDepth ?? 0) : 0,
  });

  // Attachments ride by id, never by bytes. Every file was already stored in
  // the session workspace when it was attached, so `chat.send` carries a
  // `sessionFileId` per chip and the gateway resolves it server-side. That
  // is what makes the transcript entry survive a reload and lets a later
  // turn refer to "the video already uploaded" without a second attach.
  const apiAttachments:
    | Array<{
        type?: string;
        mimeType?: string;
        sessionFileId?: string;
        attachmentId?: string;
        fileName?: string;
        name?: string;
      }>
    | undefined = (() => {
    if (!hasAttachments) {
      return undefined;
    }
    // JAISIU-types-sweep: every field is optional on the inner
    // record — both `mimeType` and `name` are optional on
    // `ChatAttachment`, so the wire row can carry blanks. Empty
    // strings fall out during the renderer's coalesce, and the
    // renderer skips chips that lack both a `sessionFileId` /
    // `attachmentId` AND a non-empty name.
    const out: Array<{
      type?: string;
      mimeType?: string;
      sessionFileId?: string;
      attachmentId?: string;
      fileName?: string;
      name?: string;
    }> = [];
    for (const att of attachments) {
      // A chip that is still storing, or that failed, MUST NOT be sent. The
      // id is what makes the file reachable server-side; without it there is
      // nothing to resolve.
      if (att.error || att.uploading) {
        continue;
      }
      if (att.sessionFileId) {
        out.push({
          type: attachmentWireType(att),
          mimeType: att.mimeType ?? "",
          sessionFileId: att.sessionFileId,
          fileName: att.name ?? "",
          name: att.name,
        });
        continue;
      }
      // Legacy upload-store handle (non-composer channels still send these).
      if (att.attachmentId) {
        out.push({
          type: attachmentWireType(att),
          mimeType: att.mimeType ?? "",
          attachmentId: att.attachmentId,
          fileName: att.name ?? "",
          name: att.name,
        });
      }
    }
    return out.length > 0 ? out : undefined;
  })();

  try {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= CHAT_SEND_MAX_RETRIES; attempt += 1) {
      try {
        await state.client.request("chat.send", {
          sessionKey: originSessionKey,
          message: msg,
          deliver: false,
          // Replay-protection key: same value on retry collapses to the
          // same in-flight run on the server.
          idempotencyKey: runId,
          // Stable, semantic client message id: the id the UI assigned to
          // the optimistic user message. The server uses this as the
          // persisted transcript entry id so `chat.history` can be
          // reconciled with the optimistic message purely by id match —
          // no content scan, no race with the embedded runner.
          clientMessageId: runId,
          attachments: apiAttachments,
          ...(state.chatComposerMode && state.chatComposerMode !== "agent"
            ? { composerMode: state.chatComposerMode }
            : {}),
        });
        return runId;
      } catch (err) {
        lastErr = err;
        if (!shouldRetryChatSend(err, attempt)) {
          break;
        }
        const delayMs = computeRetryDelayMs(err, attempt);
        await sleepMs(delayMs);
      }
    }

    const displayMessage = formatGatewayErrorForDisplay(lastErr);
    // UX-PLAN Pillar G (Gap 9): forward `lastErr.details` so the
    // chat-errors pipeline can lift a typed `ChatGuardrailFinding`
    // out of `ErrorShape.details.guardrail` and render the
    // `<chat-guardrail-banner>` with Truncate-and-retry. Without
    // this, the banner only has the legacy message-string fallback
    // and never sees the structured `currentSize`/`limit` numerics
    // — which is what the preview maths needs to build an accurate
    // head/tail window.
    const structuredInput: import("../chat/chat-errors.ts").ChatErrorInput = isGatewayRequestError(
      lastErr,
    )
      ? {
          message: lastErr.message,
          code: lastErr.code,
          retryable: lastErr.retryable,
          retryAfterMs: lastErr.retryAfterMs,
          details: lastErr.details,
        }
      : { message: displayMessage };

    // The server never persisted the failed run (no chat.history entry
    // will land). Drop the runId from pendingUserMessageIds so the
    // reconciliation loop stops hunting for it, and strip the
    // pending:true flag on the optimistic user row so the bubble does
    // not render a "pending" badge forever. Do this BEFORE appending
    // the assistant error row so the error sits at the bottom.
    {
      const pending =
        state.sessionKey.trim() === originSessionKey
          ? (state.pendingUserMessageIds ?? [])
          : readPendingUserMessageIdsForSession(
              state.pendingUserMessageIdsBySession,
              originSessionKey,
            );
      writePendingUserMessageIdsForSession(
        state.pendingUserMessageIdsBySession,
        originSessionKey,
        pending.filter((id) => id !== runId),
      );
      paintPendingUserMessageIdsIfActive(
        state,
        originSessionKey,
        readPendingUserMessageIdsForSession(state.pendingUserMessageIdsBySession, originSessionKey),
      );
      patchChatMessagesForBoundSession(state, originSessionKey, (current) =>
        current.map((m) => {
          if ((m as { id?: string }).id !== runId) return m;
          if ((m as { pending?: boolean }).pending !== true) return m;
          const { pending: _dropped, ...rest } = m as { pending?: boolean } & Record<
            string,
            unknown
          >;
          void _dropped;
          return rest;
        }),
      );
    }

    const errorAssistant = {
      role: "assistant",
      content: [
        {
          type: "text",
          text: `Error: ${formatStructuredChatErrorPlain(structuredInput)}`,
        },
      ],
      timestamp: Date.now(),
    };
    patchChatMessagesForBoundSession(state, originSessionKey, (current) => [
      ...current,
      errorAssistant,
    ]);
    if (paintRunStateOnActiveSession) {
      state.chatRunId = null;
      state.chatStream = null;
      state.chatReasoningStream = null;
      state.chatStreamStartedAt = null;
      state.chatCollectQueueDepth = 0;
      state.lastError = displayMessage;
    }
    // JAISIU-4089 — chat.send exhausted its retry budget. Tell the
    // session-health monitor so the topbar pill flips to "degraded"
    // (one failure is below the threshold, but a burst of them —
    // which is what exhausting the budget implies — crosses it on
    // the next poll OR the next send).
    if (state.noteSessionHealthFailure) {
      const code =
        isGatewayRequestError(lastErr) && typeof lastErr.code === "string"
          ? lastErr.code
          : "CHAT_SEND_FAILED";
      state.noteSessionHealthFailure(code);
    }
    return null;
  } finally {
    if (paintRunStateOnActiveSession) {
      state.chatSending = false;
    }
  }
}

export type SteerChatOutcome = "steered" | "not-streaming" | "failed";

/**
 * Inject a corrective user message into the live embedded run (Cursor-style redirect).
 * Returns "steered" when the message was injected, "not-streaming" when the run
 * ended before the steer landed (caller should fall back to a normal chat.send),
 * or "failed" for a real steer error.
 */
export async function steerChatMessage(
  state: ChatState,
  message: string,
): Promise<SteerChatOutcome> {
  if (!state.client || !state.connected) {
    return "failed";
  }
  const msg = message.trim();
  if (!msg) {
    return "failed";
  }

  const now = Date.now();
  const prevMessages = state.chatMessages;
  // Use the same `steerId` for the optimistic message and the wire idempotency
  // key so `mergeWithPendingUserMessages` (and the WS chat.history reconciliation
  // path) can collapse the canonical server copy once it arrives. Without the
  // stable id the optimistic message would only fall through to the
  // content + 60s timestamp dedupe path, which leaks duplicates whenever the
  // gateway re-anchors the entry's timestamp outside that window.
  const steerId = generateUUID();
  // Idempotent append — see `appendOptimisticUserMessage` for the contract.
  state.chatMessages = appendOptimisticUserMessage(state.chatMessages ?? [], {
    id: steerId,
    role: "user",
    content: [{ type: "text", text: msg }],
    timestamp: now,
    pending: true,
  });
  state.pendingUserMessageIds = appendPendingUserMessageId(
    state.pendingUserMessageIds ?? [],
    steerId,
  );
  state.lastError = null;
  try {
    await state.client.request("chat.steer", {
      sessionKey: state.sessionKey,
      message: msg,
      // Replay-protection key: same value on retry collapses to the
      // same in-flight steer on the server.
      idempotencyKey: steerId,
      // Stable, semantic client message id: the id the UI assigned to
      // the optimistic steered user message. The server uses this as the
      // transcript entry id so `chat.history` can be reconciled with the
      // optimistic message purely by id match.
      clientMessageId: steerId,
    });
    return "steered";
  } catch (err) {
    if (isSteerNotStreamingError(err)) {
      // The run already finished — clear the stale busy state so the caller
      // can re-dispatch this message as a fresh chat.send.
      state.chatMessages = prevMessages;
      state.chatRunId = null;
      state.chatStream = null;
      state.chatReasoningStream = null;
      state.chatStreamStartedAt = null;
      state.lastError = null;
      return "not-streaming";
    }
    const displayMessage = formatGatewayErrorForDisplay(err);
    state.lastError = displayMessage;
    return "failed";
  }
}

export type ResetChatSessionResult =
  | { ok: true; previousKey: string; nextKey: string }
  | { ok: false };

export async function resetChatSession(state: ChatState): Promise<ResetChatSessionResult> {
  if (!state.client || !state.connected) {
    return { ok: false };
  }
  const previousKey = state.sessionKey.trim();
  try {
    // JAISIU-XXXX — server may rewrite the canonical session key on reset
    // (legacy alias → canonical, or main → fresh per-agent slot). Capture the
    // response so we keep state.sessionKey in sync; otherwise the next
    // `chat.history` reads from the old key and the old entry survives.
    const res = (await state.client.request("sessions.reset", {
      key: state.sessionKey,
      confirm: true,
    })) as { ok?: boolean; key?: string; entry?: { sessionId?: string } } | null;
    const nextKey =
      res && typeof res.key === "string" && res.key.trim() ? res.key.trim() : previousKey;
    if (nextKey !== state.sessionKey.trim()) {
      state.sessionKey = nextKey;
    }
    state.chatRunId = null;
    state.chatStream = null;
    state.chatReasoningStream = null;
    state.chatStreamStartedAt = null;
    state.chatSending = false;
    state.chatCollectQueueDepth = 0;
    state.lastError = null;
    state.chatThinkingLevel = null;
    // JAISIU-XXXX — the server just told us the transcript was archived and
    // a fresh session was minted; clear the live messages so the next
    // `loadChatHistory` doesn't merge the stale transcript back in via
    // `mergeWithPendingUserMessages` + `retainUnpersistedLiveTail`. Also
    // bump the generation token so any in-flight quiet poll from before
    // the reset cannot paint the old transcript onto the empty session.
    state.chatMessages = [];
    state.pendingUserMessageIds = [];
    state.chatHistoryGeneration = (state.chatHistoryGeneration ?? 0) + 1;
    // JAISIU-XXXX — invalidate any cached Palace continuity snapshot
    // for the now-archived session. The session key may have just been
    // rewritten by the gateway (alias → canonical), so the old snapshot
    // would either point at the archived key (still readable) or at a
    // stale key (wrong session). Clear it; the next loadChatHistory will
    // re-trigger a continuity fetch on the new key.
    if ("chatPalaceContinuity" in state) {
      (state as unknown as { chatPalaceContinuity: unknown }).chatPalaceContinuity = null;
    }
    if ("chatPalaceContinuityError" in state) {
      (state as unknown as { chatPalaceContinuityError: unknown }).chatPalaceContinuityError = null;
    }
    if ("chatPalaceContinuityPending" in state) {
      (
        state as unknown as { chatPalaceContinuityPending: Set<string> | undefined }
      ).chatPalaceContinuityPending = new Set<string>();
    }
    // Drop any cached transcript for the pre/post reset keys so tab
    // switches and refresh cannot resurrect the archived thread.
    for (const key of new Set([previousKey, nextKey])) {
      if (!key) {
        continue;
      }
      writeChatMessagesForSession(state.chatMessagesBySession, key, []);
      writePendingUserMessageIdsForSession(state.pendingUserMessageIdsBySession, key, []);
      clearLiveState(state, key);
    }
    await loadChatHistory(state);
    return { ok: true, previousKey, nextKey };
  } catch (err) {
    state.lastError = formatGatewayErrorForDisplay(err);
    return { ok: false };
  }
}

export async function rewindChatBeforeMessage(
  state: ChatState,
  params: { beforeTranscriptId?: string; beforeMessageIndex?: number },
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  try {
    await state.client.request("chat.rewind", {
      sessionKey: state.sessionKey,
      ...params,
    });
    state.chatRunId = null;
    state.chatStream = null;
    state.chatReasoningStream = null;
    state.chatStreamStartedAt = null;
    await loadChatHistory(state);
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export async function regenerateChatReply(state: ChatState): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  try {
    const res = await state.client.request<{
      lastUserMessage?: string;
    }>("chat.regenerate", {
      sessionKey: state.sessionKey,
    });
    state.chatRunId = null;
    state.chatStream = null;
    state.chatReasoningStream = null;
    state.chatStreamStartedAt = null;
    await loadChatHistory(state);
    const text = res.lastUserMessage?.trim() ?? "";
    if (!text) {
      return true;
    }
    const runId = await sendChatMessage(state, text);
    return Boolean(runId);
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

/**
 * JAISIU-3297 UX-R8: optional `revert` opts turn a plain abort into
 * "stop thinking AND revert code changes from THIS prompt only." When
 * `sinceTs` is provided the controller fires `sessions.artifacts.revert`
 * after `chat.abort` settles, scoped to the pre-prompt baseline. Earlier
 * turn history is left alone — `sinceTs` is the wall-clock captured at
 * the start of the run (see `markChatRunArtifactsBaseline`).
 *
 * The revert is best-effort: a failure is logged on `state.lastError`
 * but does not flip the return value, because the user-visible
 * outcome ("the run is no longer thinking") still holds.
 */
export type AbortChatRunRevertOpts = {
  sinceTs?: number | null;
  projectId?: string | null;
};

function seedAbortedRunId(state: ChatState, runId: string | null | undefined): void {
  if (!runId) {
    return;
  }
  if (!state.abortedRunIds) {
    state.abortedRunIds = new Set();
  }
  state.abortedRunIds.add(runId);
}

function clearLocalChatRunAfterAbort(state: ChatState): void {
  const latched = state.chatRunId?.trim() || null;
  state.chatRunId = null;
  state.chatStream = null;
  state.chatReasoningStream = null;
  state.chatLiveRunHint = null;
  state.chatStreamStartedAt = null;
  state.chatSending = false;
  state.chatCollectQueueDepth = 0;
  stopChatWaitTicker(state as unknown as Parameters<typeof stopChatWaitTicker>[0]);
  clearLiveState(state, state.sessionKey);
  if (latched && Array.isArray(state.chatMessages)) {
    const trimmed = dropStreamingTail(state.chatMessages, latched);
    if (trimmed !== state.chatMessages) {
      state.chatMessages = trimmed;
    }
  }
}

async function requestChatAbort(
  state: ChatState,
  params: { sessionKey: string; runId?: string },
): Promise<void> {
  await state.client!.request(
    "chat.abort",
    params.runId
      ? { sessionKey: params.sessionKey, runId: params.runId }
      : { sessionKey: params.sessionKey },
  );
}

export async function abortChatRun(
  state: ChatState,
  revertOpts?: AbortChatRunRevertOpts,
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  const sessionKey = state.sessionKey.trim();
  const runId = state.chatRunId?.trim() || null;
  try {
    if (runId) {
      try {
        await requestChatAbort(state, { sessionKey, runId });
      } catch (err) {
        if (!isAbortRunNotFoundError(err)) {
          try {
            await requestChatAbort(state, { sessionKey });
          } catch (fallbackErr) {
            if (!isAbortRunNotFoundError(fallbackErr)) {
              state.lastError = String(fallbackErr);
              return false;
            }
          }
        }
      }
    } else {
      await requestChatAbort(state, { sessionKey });
    }
  } catch (err) {
    if (!isAbortRunNotFoundError(err)) {
      state.lastError = String(err);
      return false;
    }
  }
  seedAbortedRunId(state, runId);
  clearLocalChatRunAfterAbort(state);
  if (state.client && state.connected) {
    void loadChatHistory(state, { quiet: true, force: true, fullHistory: true });
  }
  // Run-level revert. `sinceTs` is the pre-prompt baseline already
  // recorded by the host (see app-chat.ts markChatRunArtifactsBaseline),
  // so only file changes from this prompt get rolled back.
  const sinceTs = revertOpts?.sinceTs;
  if (typeof sinceTs === "number" && Number.isFinite(sinceTs)) {
    try {
      await revertArtifactsSince(state.client, {
        sessionKey: state.sessionKey,
        sinceTs,
        ...(revertOpts?.projectId ? { projectId: revertOpts.projectId } : {}),
      });
    } catch (err) {
      state.lastError = String(err);
      // Abort succeeded — return true so the UI tears down the live run
      // indicator. The revert failure is surfaced via lastError.
    }
  }
  return true;
}

export function handleChatRunEvent(state: ChatState, payload?: ChatRunEventPayload) {
  if (!payload?.runId || !payload.sessionKey) {
    return;
  }
  if (payload.sessionKey.trim() !== state.sessionKey.trim()) {
    return;
  }
  const origin = payload.origin?.trim() ?? "dialogue";
  if (origin === "subagent_announce" || origin === "inject" || origin === "subagent") {
    if (!state.sessionBackgroundRuns) {
      state.sessionBackgroundRuns = new Set();
    }
    state.sessionBackgroundRuns.add(payload.runId);
    if (Array.isArray(state.sessionBackgroundRunIds)) {
      if (!state.sessionBackgroundRunIds.includes(payload.runId)) {
        state.sessionBackgroundRunIds = [...state.sessionBackgroundRunIds, payload.runId];
      }
    }
  }
}

export function handleChatEvent(state: ChatState, payload?: ChatEventPayload) {
  if (!payload) {
    return null;
  }
  if (payload.sessionKey.trim() !== state.sessionKey.trim()) {
    return null;
  }
  // Fleet Control staleness verification — stamp this chat event as
  // "activity observed" after the sessionKey gate. The runId-mismatch
  // path below returns early without stamping — sub-agent announces
  // must not refresh the parent's staleness clock.
  state.chatSync = {
    lastSyncedAt: state.chatSync?.lastSyncedAt ?? null,
    lastEventAt: Date.now(),
    lastWsCloseAt: state.chatSync?.lastWsCloseAt ?? null,
  };

  // Final from another run (e.g. sub-agent announce): refresh history to show new message.
  // Streaming/tool stream ordering guard for targeted tool streams.
  if (payload.runId && state.chatRunId && payload.runId !== state.chatRunId) {
    if (payload.state === "final") {
      // Sub-agent / announce finals: return "final" so app-gateway runs a
      // quiet chat.history pin for this sessionKey without touching the
      // parent's in-flight run latch.
      const isBackgroundFinal =
        payload.runId.startsWith("inject-") ||
        state.sessionBackgroundRuns?.has(payload.runId) === true;
      if (!isBackgroundFinal) {
        // Clear stale streaming state when a mismatched final arrives.
        // Background inject/announce finals must not disturb the parent latch.
        state.chatStream = null;
        state.chatReasoningStream = null;
      }
      state.sessionBackgroundRuns?.delete(payload.runId);
      if (Array.isArray(state.sessionBackgroundRunIds)) {
        state.sessionBackgroundRunIds = state.sessionBackgroundRunIds.filter(
          (id) => id !== payload.runId,
        );
      }
      // Do NOT stop the wait ticker here — `chatRunId` still points at the
      // user's in-flight run. Stopping would freeze the live counter mid-run
      // whenever a sub-agent announce final lands.
      return "final";
    }
    // Mismatched-runId chat.error must still surface to the operator — a
    // sub-agent (or any non-active run) failure is otherwise invisible
    // because we don't append the message to `chatMessages` (it belongs to
    // a different run) and we don't clear `chatRunId`/`chatStream` (the
    // user's own run is still in flight). Only set `lastError`; leave
    // streaming fields and the active runId untouched.
    if (payload.state === "error") {
      const structured = chatErrorInputFromEvent(payload);
      state.lastError = formatStructuredChatErrorPlain(structured);
    }
    return null;
  }

  if (payload.state === "progress") {
    const label = payload.progress?.label?.trim() ?? "";
    const toolName = payload.progress?.toolName?.trim() ?? "";
    // JAISIU-processing-transparency — prefer the gateway-supplied
    // label when it carries a contextually specific phrase
    // ("Reading PDF…", "Diffing merge…"); otherwise resolve a
    // friendly phrase from the tool name + args. Never fall back to
    // the bare "Running <name>…" copy.
    const hint =
      label || (toolName ? resolveToolStatusPhrase(toolName, payload.progress?.args) : "");
    if (hint) {
      state.chatLiveRunHint = hint;
    }
  } else if (payload.state === "delta") {
    const thinking = extractThinking(payload.message);
    if (typeof thinking === "string" && thinking.trim()) {
      state.chatReasoningStream = thinking;
    }
    const next = extractText(payload.message);
    if (typeof next === "string" && next.trim()) {
      const current = state.chatStream ?? "";
      if (!current || next.length >= current.length) {
        state.chatStream = next;
      }
    }
    // JAISIU-activity-always-visible: refresh the per-session live
    // snapshot so a tab-switch mid-run lands on the freshest state.
    // Without this the cache would freeze at the chat.send moment and
    // never pick up the in-progress stream / reasoning text.
    if (state.chatRunId && state.chatStreamStartedAt != null) {
      recordLiveState(state, state.sessionKey, {
        runId: state.chatRunId,
        startedAt: state.chatStreamStartedAt,
        stream: state.chatStream ?? "",
        reasoningStream: state.chatReasoningStream ?? "",
        liveRunHint: state.chatLiveRunHint ?? null,
        activeFileToolPath: state.chatActiveFileToolPath ?? null,
        mainRun: state.chatMainRun ?? null,
        compaction: state.compactionStatus ?? null,
        injectedAt: null,
        // JAISIU-chat-switch-reload — refresh alongside the rest of
        // the live fields so the per-session snapshot tracks the
        // current depth (and a tab-switch mid-queue lands on N, not
        // whatever stale value the outgoing tab left behind).
        collectQueueDepth: state.chatCollectQueueDepth ?? 0,
      });
    }
    // JAISIU-3558 — append or extend the streaming tail in
    // `state.chatMessages` so the transcript grows incrementally as
    // deltas land. Reasoning-only deltas also extend the tail so
    // thinking text survives tab switches via `chatMessagesBySession`.
    const reasoningText =
      typeof state.chatReasoningStream === "string" ? state.chatReasoningStream : "";
    const hasBodyText = typeof next === "string" && next.length > 0;
    const hasReasoningText = reasoningText.trim().length > 0;
    if (state.chatRunId && (hasBodyText || hasReasoningText)) {
      const messages = Array.isArray(state.chatMessages) ? state.chatMessages : [];
      const extended = extendStreamingTail(messages, {
        runId: state.chatRunId,
        text: hasBodyText ? next : (state.chatStream ?? ""),
        ...(hasReasoningText ? { reasoning: reasoningText } : {}),
        timestamp: Date.now(),
      });
      if (extended !== messages) {
        state.chatMessages = extended;
        writeChatMessagesForSession(
          state.chatMessagesBySession,
          payload.sessionKey.trim(),
          extended,
        );
      }
    }
  } else if (payload.state === "finalizing") {
    // Lifecycle ended without streamed body; the gateway may still append
    // the assistant row via transcript fallback. Keep the live-run latch
    // open so the thread does not snap idle between lifecycle.end and the
    // substantive chat.final.
    if (!state.chatLiveRunHint?.trim()) {
      state.chatLiveRunHint = "Finishing up…";
    }
    return "finalizing";
  } else if (payload.state === "final") {
    // JAISIU-XXXX / UX-R-XXXX — operator-explicit-abort guard. If the
    // runId was aborted (the Stop button), drop the trailing chat.final
    // so the partial message does not render as "completed". The model
    // had finished streaming by the time the abort signal landed; this
    // path is the only thing preventing the bubble from re-appearing as
    // if it succeeded.
    if (payload.runId && state.abortedRunIds && state.abortedRunIds.has(payload.runId)) {
      // Still drop the live-run state so the next render paints clean.
      state.chatStream = null;
      state.chatReasoningStream = null;
      state.chatLiveRunHint = null;
      state.chatRunId = null;
      state.chatStreamStartedAt = null;
      clearLiveState(state, state.sessionKey);
      // JAISIU-composer-pill-stuck — clear the sticky hint and any
      // still-running subagent cards so the composer footer
      // ("Subagent · …") cannot survive past the abort. The child
      // sessions are dropped server-side but the lifecycle stream may
      // not deliver a terminal event for each one — without this, the
      // pill sticks until the next tab activation.
      clearStickyLiveRunHint(state as unknown as Parameters<typeof clearStickyLiveRunHint>[0]);
      terminateOrphanSubagentsOnChatClose(
        state as unknown as Parameters<typeof terminateOrphanSubagentsOnChatClose>[0],
      );
      return "final";
    }
    state.chatStream = null;
    state.chatReasoningStream = null;
    state.chatLiveRunHint = null;
    state.chatLiveRunHint = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    // JAISIU-activity-always-visible: drop the per-session live snapshot
    // so the next tab activation paints a clean "completed" bubble
    // instead of a stale "Working…" tied to the now-closed run.
    clearLiveState(state, state.sessionKey);
    // JAISIU-composer-pill-stuck — clear the sticky hint and any
    // still-running subagent cards. The sticky is set whenever a
    // non-empty `liveRunHint` lands; without an explicit clear on run
    // close, the next render re-asserts the last latched label (a
    // "Subagent · <id>:<title>" or "Reading…" hint) and the operator
    // sees the pill persist well after the session returned. See
    // app-tool-stream.ts for the helper rationale.
    clearStickyLiveRunHint(state as unknown as Parameters<typeof clearStickyLiveRunHint>[0]);
    terminateOrphanSubagentsOnChatClose(
      state as unknown as Parameters<typeof terminateOrphanSubagentsOnChatClose>[0],
    );
    // Drain the collect-mode counter when the run that was holding up the
    // queue finalizes. We deliberately set to 0 (rather than decrement by
    // 1) because the matching `chatRunId` was the single bottleneck and
    // any messages counted against it are now free to be picked up by the
    // agent — even if the server's batched-final semantics are slightly
    // different, the operator-visible state ("Queued (n)") should snap
    // back to 0 the instant the live run closes, not on the next poll.
    if (state.chatCollectQueueDepth && state.chatCollectQueueDepth > 0) {
      state.chatCollectQueueDepth = 0;
    }
    // Stop the live-wait ticker synchronously — do not wait for Lit
    // `updated()`. A stranded interval keeps re-rendering the thread and
    // makes activity durations look like they are still measuring time.
    if ("chatWaitTicker" in state) {
      stopChatWaitTicker(state as unknown as Parameters<typeof stopChatWaitTicker>[0]);
    }
    // JAISIU-XXXX: inject-path bubble flash. The chat.inject server handler
    // stamps `runId: "inject-<messageId>"` on its broadcast payload — these
    // are the only finals that arrive outside an in-flight run. Briefly
    // raise the live-run bubble so the operator sees the row arrive rather
    // than silently appearing at the bottom of the transcript.
    if (typeof payload.runId === "string" && payload.runId.startsWith("inject-")) {
      state.chatInjectedAt = Date.now();
      // Auto-clear after the flash window so the bubble drops on its own
      // and does not latch on across subsequent user activity.
      setTimeout(() => {
        if (state.chatInjectedAt != null) {
          state.chatInjectedAt = null;
        }
      }, 1850);
    } else {
      state.chatInjectedAt = null;
    }
    // JAISIU-3558 — collapse the streaming tail into the canonical
    // assistant final in place. The helper handles the no-tail case
    // (append canonical at the end), the tail-exists case (replace in
    // place), and the replay case (strip tail, do not re-append). See
    // the docstring on `collapseStreamingTailIntoFinal` for details.
    if (payload.message && !extractAssistantErrorMessage(payload.message)) {
      const incoming = payload.message as { role?: unknown };
      if (incoming.role !== "user") {
        // JAISIU-XXXX (chat-jump-on-send): if the gateway ships the
        // assistant final message without an `id`, stamp it with the
        // active runId so the view-side `messageKey()` returns a stable
        // Lit key across the next `chat.history` poll. Without this,
        // the message is keyed by array index → DOM is torn down on
        // reconcile → scroll position visually jumps to top.
        const nextRunId = typeof payload.runId === "string" ? payload.runId : null;
        const wireTranscriptId =
          typeof payload.transcriptId === "string" && payload.transcriptId.trim()
            ? payload.transcriptId.trim()
            : undefined;
        let messageToAppend = payload.message;
        const incomingHasId = getMessageId(messageToAppend);
        if (wireTranscriptId || (!incomingHasId && nextRunId)) {
          const cloned = { ...(messageToAppend as Record<string, unknown>) };
          const stableId = wireTranscriptId ?? nextRunId!;
          if (!incomingHasId) {
            cloned.id = stableId;
          }
          cloned.transcriptId = wireTranscriptId ?? stableId;
          messageToAppend = cloned;
        }
        const existing = Array.isArray(state.chatMessages) ? state.chatMessages : [];
        const collapsedId =
          wireTranscriptId ?? getMessageId(messageToAppend) ?? nextRunId ?? undefined;
        const collapsed = collapseStreamingTailIntoFinal(existing, {
          runId: nextRunId,
          finalMessage: messageToAppend,
          ...(collapsedId ? { finalId: collapsedId } : {}),
        });
        if (collapsed !== existing) {
          state.chatMessages = collapsed;
        }
        state.lastError = null;
      }
    }
  } else if (payload.state === "aborted") {
    state.chatStream = null;
    state.chatReasoningStream = null;
    state.chatLiveRunHint = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    if ("chatWaitTicker" in state) {
      stopChatWaitTicker(state as unknown as Parameters<typeof stopChatWaitTicker>[0]);
    }
    // JAISIU-3558 — drop the in-flight streaming tail inside the
    // transcript too. Without this an aborted run would leave a
    // synthetic assistant row stranded forever.
    if (Array.isArray(state.chatMessages)) {
      const trimmed = dropStreamingTail(state.chatMessages, payload.runId);
      if (trimmed !== state.chatMessages) {
        state.chatMessages = trimmed;
      }
    }
    // JAISIU-activity-always-visible: drop the per-session live snapshot
    // so the operator's next activation does not see a stale "Working…"
    // bubble for an aborted run.
    clearLiveState(state, state.sessionKey);
  } else if (payload.state === "error") {
    state.chatStream = null;
    state.chatReasoningStream = null;
    state.chatLiveRunHint = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    if ("chatWaitTicker" in state) {
      stopChatWaitTicker(state as unknown as Parameters<typeof stopChatWaitTicker>[0]);
    }
    // JAISIU-3558 — drop the in-flight streaming tail inside the
    // transcript so an errored run does not strand a synthetic
    // assistant row. See the `aborted` branch above for the same
    // treatment.
    if (Array.isArray(state.chatMessages)) {
      const trimmed = dropStreamingTail(state.chatMessages, payload.runId);
      if (trimmed !== state.chatMessages) {
        state.chatMessages = trimmed;
      }
    }
    const structured = chatErrorInputFromEvent(payload);
    state.lastError = formatStructuredChatErrorPlain(structured);
    // JAISIU-activity-always-visible: drop the snapshot for the same
    // reason as the aborted branch — the run is over.
    clearLiveState(state, state.sessionKey);
  }
  return payload.state;
}
